import { Box, Button, Icon, TextField } from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import React, { useEffect, useState } from 'react';
import AptaTable from 'examples/AptaTable';
import MDTypography from 'components/MDTypography';
import axios from 'axios';
import { URL } from 'constants/constant';
import Swal from 'sweetalert2';
import { SearchInput } from 'layouts/speakers';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // For true status
import CancelIcon from '@mui/icons-material/Cancel'; // For false status
import Tooltip from '@mui/material/Tooltip'; // For showing scanner/exhibitor name on hover
import * as XLSX from 'xlsx';

const Attendence = () => {
    const [data, setData] = useState([]);
    const [search, setSearch] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const adminType = localStorage.getItem('type');

    const getdata = async () => {
        try {
            const response = await axios.get(`${URL}/api/admin/attendance/list`);
            if (response.data) {
                // Flatten the data to show each user's attendance with multiple scanners/exhibitors
                const flattened = response.data.data.flatMap(user => {
                    const userBase = {
                        _id: user._id,
                        fullName: user.fullName,
                    };

                    // Combine scanner and exhibitor attendance into a single list
                    const allAttendance = [];
                    user.exhibitorAttendance.forEach(exhibitor => {
                        allAttendance.push({
                            type: 'exhibitor',
                            name: exhibitor.exhibitorName,
                            aId: exhibitor?.exhibitorId,
                            status: exhibitor.attendance.status,
                            id: exhibitor.attendance?._id
                        });
                    });
                    user.scannerAttendance.forEach(scanner => {
                        allAttendance.push({
                            type: 'scanner',
                            name: scanner.scannerName,
                            aId: scanner?.scannerId,
                            status: scanner.attendance.status,
                            id: scanner.attendance?._id
                        });
                    });
                    // Return an object for each user that includes their attendance
                    return {
                        ...userBase,
                        allAttendance: allAttendance
                    };
                });
                setData(flattened);
            }
        } catch (err) {
            console.error("Error fetching data:", err);
            Swal.fire("Error!", "Failed to fetch data.", "error");
        }
    };

    useEffect(() => {
        getdata();
    }, []);

    useEffect(() => {
        if (!search) {
            setFilteredData(data);
        } else {
            setFilteredData(
                data.filter(
                    (user) =>
                        user?.fullName?.toLowerCase().includes(search.toLowerCase()) ||
                        user?.allAttendance?.some(
                            (att) => att.name.toLowerCase().includes(search.toLowerCase())
                        )
                )
            );
        }
    }, [search, data]);

    // Dynamically generate columns based on unique scanner/exhibitor names
    const generateColumns = () => {
        const uniqueNames = new Set();
        data.forEach(user => {
            user.allAttendance.forEach(att => {
                uniqueNames.add(att.name);
            });
        });

        const dynamicColumns = Array.from(uniqueNames).map(name => ({
            Header: name, // This will be the column header
            accessor: name.toLowerCase().replace(/[^a-z0-9]/g, ''), // Simple accessor for filtering
        }));

        return [{ Header: 'User', accessor: 'fullName' }, ...dynamicColumns];
    };

    const columns = generateColumns(); // Generate columns once data is available
    const exportToExcel = () => {
        const headers = ['User', ...columns.slice(1).map(col => col.Header)];
        const rows = filteredData.map(user => {
            const row = {};
            row['User'] = user?.fullName;

            columns.slice(1).forEach(col => {
                const att = user.allAttendance.find(a => a.name === col.Header);
                row[col.Header] = att ? (att.status ? 'Present' : 'Absent') : 'N/A';
            });

            return row;
        });


        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");

        XLSX.writeFile(workbook, "Attendance.xlsx");
    };
    return (
        <DashboardLayout>
            <DashboardNavbar />
            <SearchInput searchQuery={search} handleSearchChange={(e) => {
                setSearch(e.target.value);
            }} />
            <Box display={'flex'} mb={3} justifyContent={'end'}>
                <Button style={{ color: 'white', backgroundColor: '#2ecc71' }}
                    onClick={exportToExcel}
                    variant='contained' >Download Excel</Button>
            </Box>
            <AptaTable
                columns={columns.map(col => col.Header)} // Pass only the Headers to AptaTable
                rows={filteredData?.map((user) => {
                    const row = {
                        User: <MDTypography variant="caption">{user?.fullName}</MDTypography>,
                    };

                    // Populate dynamic columns with status icons
                    columns.slice(1).forEach(col => { // Skip the 'User' column
                        const attendanceEntry = user.allAttendance.find(att => att.name === col.Header);
                        row[col.Header] = attendanceEntry ? (
                            <Tooltip title={`${attendanceEntry.name} - ${attendanceEntry.status ? 'Present' : 'Absent'}`}>
                                {attendanceEntry.status ? (
                                    <CheckCircleIcon
                                        onClick={async () => {
                                            if (adminType === 'superadmin') {
                                                try {
                                                    const payload =
                                                        attendanceEntry.type === 'exhibitor'
                                                            ? { exhibitorAttendanceId: attendanceEntry.id }
                                                            : { scannerAttendanceId: attendanceEntry.id };

                                                    const result = await Swal.fire({
                                                        title: 'Are you sure?',
                                                        text: `Delete ${attendanceEntry.type} attendance?`,
                                                        icon: 'warning',
                                                        showCancelButton: true,
                                                        confirmButtonColor: '#d33',
                                                        cancelButtonColor: '#3085d6',
                                                        confirmButtonText: 'Yes, delete it!'
                                                    });

                                                    if (result.isConfirmed) {
                                                        await axios.delete(`${URL}/api/admin/attendance`, {
                                                            data: payload,
                                                        });
                                                        Swal.fire('Deleted!', 'Attendance has been removed.', 'success');
                                                        getdata(); // Refresh data
                                                    }
                                                } catch (err) {
                                                    console.error("Delete failed", err);
                                                    Swal.fire('Error', 'Failed to delete attendance.', 'error');
                                                }
                                            }
                                        }}
                                        color="success"
                                        style={{ height: 25, width: 25, cursor: 'pointer' }}
                                    />
                                ) : (
                                    <CancelIcon
                                        color="error"
                                        onClick={async () => {
                                            if (adminType === 'superadmin') {

                                                try {
                                                    const payload =
                                                        attendanceEntry.type === 'exhibitor'
                                                            ? { exhibitorId: attendanceEntry.aId, userId: user?._id }
                                                            : { scannerId: attendanceEntry.aId, userId: user?._id };
                                                    const result = await Swal.fire({
                                                        title: 'Are you sure?',
                                                        text: `Mark ${attendanceEntry.type} attendance?`,
                                                        icon: 'warning',
                                                        showCancelButton: true,
                                                        confirmButtonColor: '#d33',
                                                        cancelButtonColor: '#3085d6',
                                                        confirmButtonText: 'Yes'
                                                    });

                                                    const postUrl = attendanceEntry?.type === 'exhibitor' ? `${URL}/api/admin/exhibitor-attendance` : `${URL}/api/admin/attendance`
                                                    if (result.isConfirmed) {
                                                        await axios.post(postUrl, {
                                                            ...payload,
                                                        });
                                                        Swal.fire('Marked!', 'Attendance has been marked.', 'success');
                                                        getdata(); // Refresh data
                                                    }
                                                } catch (err) {
                                                    console.error("Mark failed", err);
                                                    Swal.fire('Error', 'Failed to ,mark attendance.', 'error');
                                                }

                                            }
                                        }}
                                        style={{ height: 25, width: 25, cursor: 'pointer' }}

                                    />
                                )}
                            </Tooltip>
                        ) : (
                            <MDTypography variant="caption" color="textSecondary">N/A</MDTypography>
                        );

                    });
                    return row;
                })}
            />
        </DashboardLayout>
    );
};

export default Attendence;